import { supabase } from "./supabase";
import { formatCurrency } from "../utils/helpers";

// Service for deleting ad performance data by date
export const dateDeleteService = {
  // Get summary of data for a specific date before deletion
  async getDateSummary(date) {
    try {
      console.log(`🔍 Getting data summary for date: ${date}`);

      const { data, error } = await supabase.rpc("get_date_data_summary", {
        p_date: date,
      });

      if (error) {
        console.error("❌ Error getting date summary:", error);
        throw new Error(`Failed to get data summary: ${error.message}`);
      }

      const summary = data;

      return {
        date: summary.date,
        recordCount: summary.record_count || 0,
        affiliateCount: summary.affiliate_count || 0,
        totalSpend: parseFloat(summary.total_spend || 0),
        totalRevenue: parseFloat(summary.total_revenue || 0),
        affiliates: summary.affiliates || [],
        hasData: summary.has_data || false,
        formattedSpend: formatCurrency(parseFloat(summary.total_spend || 0)),
        formattedRevenue: formatCurrency(
          parseFloat(summary.total_revenue || 0)
        ),
      };
    } catch (err) {
      console.error("❌ Date summary service error:", err);
      throw new Error(`Failed to get date summary: ${err.message}`);
    }
  },

  // Delete all data for a specific date
  async deleteDataForDate(date, onProgress = null) {
    try {
      console.log(`🗑️ Starting deletion for date: ${date}`);

      if (onProgress) {
        onProgress({
          stage: "preparing",
          message: `Preparing to delete data for ${date}...`,
          progress: 10,
        });
      }

      // First get summary to show what will be deleted
      const summary = await this.getDateSummary(date);

      if (!summary.hasData) {
        return {
          success: true,
          deletedCount: 0,
          date: date,
          message: `No data found for ${date}`,
          summary: summary,
        };
      }

      if (onProgress) {
        onProgress({
          stage: "deleting",
          message: `Deleting ${summary.recordCount} records for ${date}...`,
          progress: 30,
        });
      }

      // Perform the deletion
      const { data, error } = await supabase.rpc(
        "delete_ad_performance_by_date",
        {
          p_date: date,
        }
      );

      if (error) {
        console.error("❌ Error deleting data:", error);
        throw new Error(`Failed to delete data: ${error.message}`);
      }

      if (onProgress) {
        onProgress({
          stage: "cleaning",
          message: "Cleaning up related data...",
          progress: 70,
        });
      }

      // Small delay to show progress
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (onProgress) {
        onProgress({
          stage: "completed",
          message: `Successfully deleted ${data.deleted_count} records`,
          progress: 100,
        });
      }

      console.log(`✅ Successfully deleted data for ${date}:`, data);

      return {
        success: data.success,
        deletedCount: data.deleted_count,
        date: data.date,
        affectedAffiliates: data.affected_affiliates || [],
        processingTimeMs: data.processing_time_ms,
        message: data.message,
        summary: summary,
      };
    } catch (err) {
      console.error("❌ Date deletion service error:", err);

      if (onProgress) {
        onProgress({
          stage: "error",
          message: `Error: ${err.message}`,
          progress: 0,
        });
      }

      throw new Error(`Failed to delete data: ${err.message}`);
    }
  },

  // Get list of available dates with data (for date picker)
  async getAvailableDates(limit = 100) {
    try {
      console.log("📅 Getting available dates with data...");

      const { data, error } = await supabase
        .from("ad_performance")
        .select("date")
        .not("date", "is", null)
        .order("date", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("❌ Error getting available dates:", error);
        throw new Error(`Failed to get available dates: ${error.message}`);
      }

      // Get unique dates
      const uniqueDates = [...new Set(data.map((row) => row.date))].sort(
        (a, b) => new Date(b) - new Date(a)
      );

      console.log(`📅 Found ${uniqueDates.length} unique dates with data`);

      return uniqueDates;
    } catch (err) {
      console.error("❌ Available dates service error:", err);
      throw new Error(`Failed to get available dates: ${err.message}`);
    }
  },

  // Format date for display
  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (err) {
      return dateString;
    }
  },

  // Format date for input (YYYY-MM-DD)
  formatDateForInput(dateString) {
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    } catch (err) {
      return dateString;
    }
  },
};
